/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';

import { Box, Card, Divider, Grid, Icon, IconButton, Link, Paper, Tooltip, Typography } from '@mui/material';
import { ChipStatus, IChipsSettingProps } from '../chip-status/ChipStatus';
import { TextFieldReadOnly } from '../text-field-readonly/TextFieldReadOnly';
import { formatDate, formatDateTime } from '../../utils';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';

const KANBAN_COLUMN_WIDTH = 280;
const KANBAN_INITIAL_VISIBLE_CARDS = 10;
const KANBAN_VISIBLE_CARDS_STEP = 10;

interface IKanbanListProps {
    identifierField: string;
    attributes: IKanbanCardColumns[];
    cards: any[];
    statusList: any[];
    cardSize?: number | 12;
    actions?: IKanbanCardActions[] | undefined;
    columnTitleSetting?: IKanbanColumnTitleSetting[];
    setRowExpanded: (data: any) => void;
    setCardMoved: (data: any) => void;
}

export interface IKanbanCardColumns {
    field: string;
    label: string;
    size: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    type: 'text' | 'boolean' | 'currency' | 'date' | 'dateTime' | 'decimal' | 'component' | 'percent' | 'number';
    visible: boolean | true;
    isChip: boolean | false;
    chipSettings?: IChipsSettingProps[];
    hasFieldLine: boolean | false;
    hasLabelChip?: boolean | false;
    hasToolTip?: boolean | true;
    colorValue?: string;
    noWrap?: boolean;
}
export interface IKanbanCardActions {
    id: string;
    toolTip: string;
    variantButton?: string;
    icon: string;
    iconColor: 'inherit' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | undefined;
    setAction: (data: any) => void;
}
export interface IKanbanColumnTitleSetting {
    value: string;
    iconName: string;
    iconColor: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | undefined;
}
export const KanbanList: React.FC<IKanbanListProps> = ({
    identifierField,
    attributes,
    cards,
    statusList,
    cardSize,
    actions,
    setRowExpanded,
    setCardMoved,
}) => {
    const [tasksByStatus, setTasksByStatus] = useState<{ [statusId: string]: any[] }>({});
    const [visibleCardsByStatus, setVisibleCardsByStatus] = useState<{ [statusId: string]: number }>({});

    const handleLoadCards = useCallback(() => {
        const categorizedTasks: { [key: string]: any[] } = {};
        const statusIds = new Set(statusList.map((status) => status.id));
        const statusIdByValue = new Map(statusList.map((status) => [status.value, status.id]));
        const defaultStatusId = statusList[0]?.id;

        cards.forEach((task) => {
            const statusLabelField = identifierField === 'customerStatusId' ? 'customerStatus' : 'status';
            const identifierValue = statusIds.has(task[identifierField])
                ? task[identifierField]
                : statusIdByValue.get(task[statusLabelField]) || defaultStatusId;

            if (!identifierValue) {
                return;
            }

            if (!categorizedTasks[identifierValue]) {
                categorizedTasks[identifierValue] = [];
            }
            categorizedTasks[identifierValue].push(task);
        });

        setTasksByStatus(categorizedTasks);
        setVisibleCardsByStatus(
            Object.keys(categorizedTasks).reduce((visibleCards, statusId) => ({
                ...visibleCards,
                [statusId]: KANBAN_INITIAL_VISIBLE_CARDS,
            }), {})
        );
    }, [cards, identifierField, statusList]);

    useEffect(() => {
        handleLoadCards();
    }, [handleLoadCards]);

    const handleShowMoreCards = useCallback((statusId: string) => {
        setVisibleCardsByStatus((oldVisibleCards) => ({
            ...oldVisibleCards,
            [statusId]: (oldVisibleCards[statusId] || KANBAN_INITIAL_VISIBLE_CARDS) + KANBAN_VISIBLE_CARDS_STEP,
        }));
    }, []);

    const handleTaskMove = useCallback(
        (result: any) => {
            if (!result.destination) return;

            const sourceColumn = result.source.droppableId;
            const destinationColumn = result.destination.droppableId;

            if (sourceColumn === destinationColumn) {
                const updatedTasks = [...tasksByStatus[sourceColumn]];
                const [movedTask] = updatedTasks.splice(result.source.index, 1);
                updatedTasks.splice(result.destination.index, 0, movedTask);
                setTasksByStatus((prevTasks) => ({ ...prevTasks, [sourceColumn]: updatedTasks }));
            } else {
                const sourceTasks = tasksByStatus[sourceColumn] ? [...tasksByStatus[sourceColumn]] : [];
                const destinationTasks = tasksByStatus[destinationColumn] ? [...tasksByStatus[destinationColumn]] : [];

                const [movedTask] = sourceTasks.splice(result.source.index, 1);
                destinationTasks.splice(result.destination.index, 0, movedTask);

                setTasksByStatus((prevTasks) => ({
                    ...prevTasks,
                    [sourceColumn]: sourceTasks,
                    [destinationColumn]: destinationTasks,
                }));
            }

            const jsonData = {
                id: result.draggableId,
                [identifierField]: result.destination.droppableId,
                index: result.destination.index,
            };

            setCardMoved(jsonData);
        },
        [tasksByStatus, setCardMoved]
    );

    return (
        <Box sx={{ display: 'flex', width: '100%', height: '100%', minHeight: 0 }}>
            <DragDropContext onDragEnd={handleTaskMove}>
                {/* {list.length > 0 ? ( */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', height: '100%', minHeight: 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', flex: 1, minHeight: 0, gap: 1, width: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
                        {/* <Grid container spacing={1}> */}
                        {statusList.map((status, index) => (
                            <React.Fragment key={index}>
                                <Droppable droppableId={status.id}>
                                    {(provided, snapshot) => (
                                        <Box width={KANBAN_COLUMN_WIDTH} minWidth={KANBAN_COLUMN_WIDTH} sx={{ display: 'flex', height: '100%', minHeight: 0 }}>
                                            <Paper
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'flex-start',
                                                    gap: 1,
                                                    p: 1,
                                                    width: '100%',
                                                    height: '100%',
                                                    maxHeight: '100%',
                                                    minHeight: 0,
                                                    overflow: 'hidden',
                                                    border: '1px solid',
                                                    borderColor: snapshot.isDraggingOver ? 'secondary.main' : 'divider',
                                                    bgcolor: snapshot.isDraggingOver ? 'action.selected' : 'background.paper',
                                                    boxShadow: snapshot.isDraggingOver ? '0 8px 20px rgba(91, 178, 221, 0.16)' : 'none',
                                                    transition: 'background-color .2s ease, border-color .2s ease, box-shadow .2s ease'
                                                }}
                                                elevation={3}
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                            >
                                                <Box sx={{ flexShrink: 0, pt: 0.5, px: 0.25 }}>
                                                    <Tooltip title={status.value} placement='bottom-start'>
                                                        <Typography
                                                            id={status.id}
                                                            variant='caption'
                                                            component='div'
                                                            noWrap
                                                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                        >
                                                            {status.value + ' [' + (tasksByStatus[status.id]?.length || 0) + ']'}
                                                        </Typography>
                                                    </Tooltip>
                                                </Box>
                                                <Divider />
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1,
                                                        flex: 1,
                                                        minHeight: 0,
                                                        overflowY: 'scroll',
                                                        overflowX: 'hidden',
                                                        pr: 0.5,
                                                        pb: 1,
                                                        scrollbarWidth: 'thin',
                                                        '&::-webkit-scrollbar': {
                                                            width: 8,
                                                        },
                                                        '&::-webkit-scrollbar-thumb': {
                                                            borderRadius: 8,
                                                            bgcolor: 'divider',
                                                        },
                                                        '&::-webkit-scrollbar-track': {
                                                            bgcolor: 'transparent',
                                                        },
                                                    }}
                                                >
                                                    {(tasksByStatus[status.id] || []).slice(0, visibleCardsByStatus[status.id] || KANBAN_INITIAL_VISIBLE_CARDS).map((task, index) => (
                                                        <Draggable key={task.id.toString()} draggableId={task.id.toString()} index={index}>
                                                            {(provided) => (
                                                                <Cards
                                                                    key={index}
                                                                    attributes={attributes}
                                                                    row={task}
                                                                    setRowExpanded={setRowExpanded}
                                                                    cardSize={cardSize}
                                                                    actions={actions}
                                                                    provided1={provided}
                                                                />
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                    {(tasksByStatus[status.id]?.length || 0) > (visibleCardsByStatus[status.id] || KANBAN_INITIAL_VISIBLE_CARDS) && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0, pt: 0.5 }}>
                                                            <Link
                                                                component='button'
                                                                type='button'
                                                                underline='hover'
                                                                color='primary'
                                                                variant='caption'
                                                                onClick={() => handleShowMoreCards(status.id)}
                                                            >
                                                                Carregar mais {KANBAN_VISIBLE_CARDS_STEP}
                                                            </Link>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Paper>
                                        </Box>
                                    )}
                                </Droppable>
                            </React.Fragment>
                        ))}
                    </Box>
                    {/* </Grid> */}
                </Box>
                {/* ) : (
                    <Card sx={{ display: 'flex', borderRadius: 1, width: '100%', p: 2, pb: 2, gap: 1, cursor: 'auto', transition: 'all .3s', ':hover': { opacity: .7 }, boxShadow: 'rgb(0 0 0 / 14%) 0rem 0.25rem 1.25rem 0rem, rgb(64 64 64 / 40%) 0rem 0.4375rem 0.625rem -0.3125rem' }}>
                        <Grid display={'flex'} container spacing={0} >
                            <Grid display={'flex'} item xs={12} md={12} justifyContent={'center'}>
                                <Chip icon={<Icon>search_off</Icon>} label={'No records were found for the selected search criteria.'} color={'primary'} variant="outlined" size='medium' />
                            </Grid>
                        </Grid>
                    </Card>
                ) */}
                {/* } */}
            </DragDropContext >
        </Box >
    );
};

function Cards(
    props: {
        attributes: IKanbanCardColumns[],
        row: any[],
        setRowExpanded: (data: any) => void,
        cardSize?: number | 12,
        actions?: IKanbanCardActions[] | undefined,
        provided1: any
    }) {

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleExpandRow = useCallback((row: any, isOpen: boolean) => {
        setOpen(oldValue => !oldValue);
        setLoading(true);
        row ?? props.setRowExpanded(row);
        !isOpen ? props.setRowExpanded(row) : undefined;
    }, []);

    const handleAction = useCallback((action: any) => {
        action();
        setOpen(false);

    }, []);

    const handleCardClick = useCallback((action: any, row: any, isOpen: boolean) => {
        if (action) {
            handleAction(action);
        }
        else {
            // props.hasChildren ? handleExpandRow(row, isOpen) : undefined;
        }
    }, []);

    return (
        <React.Fragment>
            <Card
                ref={props.provided1.innerRef}
                {...props.provided1.draggableProps}
                {...props.provided1.dragHandleProps}
                sx={{
                    display: 'flex',
                    flexShrink: 0,
                    borderRadius: 1,
                    minWidth: 0,
                    width: '100%',
                    boxSizing: 'border-box',
                    p: 1.5,
                    cursor: 'pointer',
                    transition: 'all .3s',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: 'none',
                    ':hover': {
                        borderColor: 'secondary.main',
                        boxShadow: '0 6px 16px rgba(16, 24, 40, 0.06)'
                    }
                }}>
                <Grid container spacing={1.25} >
                    {props.attributes.map((column: IKanbanCardColumns, index: any) => {
                        const label = column.label;
                        let value = '';

                        for (let i = 0; i < Object.values<any>(props.row).length; i++) {
                            if (Object.keys(props.row)[i].valueOf() === column.field) {
                                value = Object.values<any>(props.row)[i];
                                break;
                            }
                        }
                        /* eslint-disable indent */
                        switch (column.type) {
                            case 'currency':
                                value = 'R$ ' + value;
                                break;
                            case 'boolean':
                                value = String(value);
                                break;

                            case 'date':
                                value = String(formatDate(value));
                                break;

                            case 'dateTime':
                                value = String(formatDateTime(value));
                                break;

                            case 'decimal':
                                value = Number(value).toFixed(2);
                                break;

                            case 'percent':
                                value = value ? (parseFloat(value)).toFixed(2) + '%' : '0,00%'; // Converte para porcentagem com duas casas decimais
                                break;

                            default:
                                break;
                        }
                        if (column.visible) {
                            return (
                                <React.Fragment key={index}>
                                    <Grid item xs={12} md={column.size}>
                                        <TextFieldReadOnly
                                            id={index}
                                            label={label}
                                            value={!column.isChip ? value : <ChipStatus label={label} value={value} chipSettings={column.chipSettings}/>}
                                            hasDivider={column.hasFieldLine}
                                            hasToolTip={column.hasToolTip}
                                            colorValue={column.colorValue}
                                            noWrap={column.noWrap ?? true}
                                        />
                                    </Grid>
                                </React.Fragment>
                            );
                        }
                    })}
                    <Grid item xs={12} md={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Box display='flex' justifyContent='flex-start' sx={{ display: 'flex', overflowX: 'auto', pt: 0.25 }}>
                            {props.actions ? props.actions.map((action, index) => {
                                return (
                                    <Tooltip title={action.toolTip} key={index}>
                                        <IconButton
                                            id={action.id}
                                            key={index}
                                            size='small'
                                            onClick={() => handleCardClick(action.setAction(props.row), null, false)}
                                        >
                                            <Icon fontSize='small' color={action.iconColor}>{action.icon}</Icon>
                                        </IconButton>
                                    </Tooltip>
                                );
                            }) : undefined}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={12}>
                        {/* {loading ? (
                            <LinearProgress sx={{ borderRadius: 3 }} />
                        ) : (
                            undefined
                        )} */}
                    </Grid>
                </Grid>
            </Card>
        </React.Fragment>
    );
}
